import {
  Box,
  Checkbox,
  Drawer,
  FormControlLabel,
  Paper,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { differenceInSeconds, formatDistanceToNow } from "date-fns";
import { useTranslation } from "react-i18next";
import { apiUrl } from "../../common/api/config";
import Button from "../../common/components/Button";
import { SPACING } from "../../common/constants/spacing";

const sectionStyle = {
  px: 3,
  py: 3,
};

const formatLastSaved = (lastSaved) => {
  if (!lastSaved) return "";

  const diff = differenceInSeconds(new Date(), lastSaved);

  if (diff < 5) return "Saved just now";

  return `Saved ${formatDistanceToNow(lastSaved, { addSuffix: true })}`;
};

export default function TemplateCustomizer({
  formData,
  errors,
  handleChange,
  handleSubmit,
  buttonLabel = "Save",
  saveStatus,
  lastSaved,
  mobileOpen,
  onMobileClose,
}) {
  const { t } = useTranslation("reports");
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("md"));

  const content = (
    <Box
      component={handleSubmit ? "form" : "div"}
      onSubmit={handleSubmit}
      sx={{ overflowY: "auto", height: "100%" }}
    >
      {/* Save Status Indicator - only show for auto-save mode */}
      {saveStatus && (
        <Box
          sx={{
            px: 3,
            pt: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 1,
            minHeight: "56px",
          }}
          data-testid="template-save-status"
        >
          {saveStatus === "saving" && (
            <>
              <Typography
                variant="bodySm"
                sx={{ color: "text.secondary", fontSize: "13px" }}
                data-testid="template-saving-indicator"
              >
                {t("templateCustomizer.saveStatus.saving")}
              </Typography>
            </>
          )}
          {saveStatus === "saved" && (
            <>
              <Typography
                variant="bodySm"
                sx={{ color: "text.secondary", fontSize: "13px" }}
                data-testid="template-saved-indicator"
              >
                {formatLastSaved(lastSaved)}
              </Typography>
            </>
          )}
        </Box>
      )}

      <Box sx={sectionStyle}>
        <Typography
          variant="h6"
          sx={{ fontWeight: 600, mb: SPACING.sm, fontSize: "16px" }}
        >
          {t("templateCustomizer.general")}
        </Typography>
        <TextField
          fullWidth
          label={t("templateCustomizer.reportTitle")}
          name="title"
          value={formData.title}
          onChange={handleChange}
          error={Boolean(errors.title)}
          helperText={errors.title}
          margin="normal"
          data-testid="template-title-input"
        />
      </Box>

      <Box sx={sectionStyle}>
        {/* Company Details Section */}
        <Typography
          variant="h6"
          sx={{ fontWeight: 600, mb: SPACING.sm, fontSize: "16px" }}
        >
          {t("templateCustomizer.companyDetails")}
        </Typography>

        <TextField
          fullWidth
          label={t("templateCustomizer.companyName")}
          name="company_name"
          value={formData.company_name}
          onChange={handleChange}
          error={Boolean(errors.company_name)}
          helperText={errors.company_name}
          margin="normal"
          data-testid="template-company-name-input"
        />

        <TextField
          fullWidth
          label={t("templateCustomizer.address")}
          name="address"
          value={formData.address}
          onChange={handleChange}
          error={Boolean(errors.address)}
          helperText={errors.address}
          margin="normal"
          placeholder={t("templateCustomizer.addressPlaceholder")}
        />

        <TextField
          fullWidth
          label={t("templateCustomizer.location")}
          name="location"
          value={formData.location}
          onChange={handleChange}
          error={Boolean(errors.location)}
          helperText={errors.location}
          margin="normal"
          placeholder={t("templateCustomizer.locationPlaceholder")}
        />
      </Box>

      <Box sx={sectionStyle}>
        <Typography
          variant="h6"
          sx={{ fontWeight: 600, mb: SPACING.sm, fontSize: "16px" }}
        >
          {t("templateCustomizer.logo")}
        </Typography>
        {formData.logo && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: 1,
            }}
          >
            <img
              src={
                typeof formData.logo === "string"
                  ? formData.logo.startsWith("http")
                    ? formData.logo
                    : `${apiUrl}${formData.logo}` // handle relative media paths
                  : URL.createObjectURL(formData.logo) // File object preview
              }
              alt="Report logo"
              style={{ maxHeight: 100, borderRadius: 4 }}
            />
          </Box>
        )}

        <Button variant="outlined" component="label" sx={{ mt: SPACING.sm }}>
          {t("templateCustomizer.uploadLogo")}
          <input
            type="file"
            name="logo"
            hidden
            onChange={handleChange}
            accept="image/*"
          />
        </Button>

        {errors.logo && (
          <Typography variant="bodySm" color="error" sx={{ mt: SPACING.sm }}>
            {errors.logo[0]}
          </Typography>
        )}
      </Box>

      <Box sx={sectionStyle}>
        <Typography
          variant="h6"
          sx={{ fontWeight: 600, mb: SPACING.sm, fontSize: "16px" }}
        >
          {t("templateCustomizer.reportContent")}
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {/* Project Summary Section */}
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.include_project_summary}
                onChange={handleChange}
                name="include_project_summary"
                data-testid="template-project-summary-checkbox"
              />
            }
            label={t("templateCustomizer.includeProjectSummary")}
          />

          {formData.include_project_summary && (
            <Box
              sx={{ pl: 4, display: "flex", flexDirection: "column", gap: 0.5 }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.include_total_assets}
                    onChange={handleChange}
                    name="include_total_assets"
                    data-testid="template-total-assets-checkbox"
                  />
                }
                label={t("templateCustomizer.totalAssets")}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.include_total_impressions}
                    onChange={handleChange}
                    name="include_total_impressions"
                    data-testid="template-total-impressions-checkbox"
                  />
                }
                label={t("templateCustomizer.totalImpressions")}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.include_total_royalty_revenue}
                    onChange={handleChange}
                    name="include_total_royalty_revenue"
                    data-testid="template-total-royalty-revenue-checkbox"
                  />
                }
                label={t("templateCustomizer.totalRoyaltyRevenue")}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.include_total_impression_revenue}
                    onChange={handleChange}
                    name="include_total_impression_revenue"
                    data-testid="template-total-impression-revenue-checkbox"
                  />
                }
                label={t("templateCustomizer.totalImpressionRevenue")}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.include_total_revenue}
                    onChange={handleChange}
                    name="include_total_revenue"
                    data-testid="template-total-revenue-checkbox"
                  />
                }
                label={t("templateCustomizer.totalRevenue")}
              />
            </Box>
          )}

          {/* Asset Breakdown Section */}
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.include_asset_breakdown}
                onChange={handleChange}
                name="include_asset_breakdown"
                data-testid="template-asset-breakdown-checkbox"
              />
            }
            label={t("templateCustomizer.includeAssetBreakdown")}
            sx={{ mt: SPACING.sm }}
          />

          {formData.include_asset_breakdown && (
            <Box
              sx={{ pl: 4, display: "flex", flexDirection: "column", gap: 0.5 }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.include_sales_revenue}
                    onChange={handleChange}
                    name="include_sales_revenue"
                    data-testid="template-sales-revenue-checkbox"
                  />
                }
                label={t("templateCustomizer.salesRevenue")}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.include_impressions}
                    onChange={handleChange}
                    name="include_impressions"
                    data-testid="template-impressions-checkbox"
                  />
                }
                label={t("templateCustomizer.impressions")}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.include_impressions_revenue}
                    onChange={handleChange}
                    name="include_impressions_revenue"
                    data-testid="template-impressions-revenue-checkbox"
                  />
                }
                label={t("templateCustomizer.impressionsRevenue")}
              />
            </Box>
          )}
        </Box>
      </Box>

      <Box sx={sectionStyle}>
        <Typography
          variant="h6"
          sx={{ fontWeight: 600, mb: SPACING.sm, fontSize: "16px" }}
        >
          {t("templateCustomizer.footer")}
        </Typography>

        <TextField
          fullWidth
          label={t("templateCustomizer.notes")}
          name="notes"
          value={formData.notes}
          onChange={handleChange}
          error={Boolean(errors.notes)}
          helperText={errors.notes || t("templateCustomizer.notesHelper")}
          margin="normal"
          multiline
          rows={6}
          placeholder={t("templateCustomizer.notesPlaceholder")}
          data-testid="template-notes-input"
        />
      </Box>

      {handleSubmit && (
        <Box sx={sectionStyle}>
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              size="lg"
              fullWidth
              sx={{ mt: 3 }}
            >
              {buttonLabel}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );

  if (isMobile) {
    return (
      <Drawer
        variant="temporary"
        anchor="right"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{ keepMounted: true }}
        sx={{ "& .MuiDrawer-paper": { width: "85vw", maxWidth: 350 } }}
      >
        {content}
      </Drawer>
    );
  }

  return (
    <Paper
      elevation={2}
      sx={{
        position: "fixed",
        right: 0,
        top: "64px",
        height: "calc(100vh - 64px)",
        width: "350px",
        borderLeft: "1px solid",
        borderColor: "divider",
        overflowY: "auto",
        overflowX: "hidden",
        zIndex: 10,
      }}
    >
      {content}
    </Paper>
  );
}
